/**
 * <niyo-size-guide> — the product-page "Find your size" dialog
 * (blocks/niyo-size-guide.liquid).
 *
 * Opens a native <dialog> (Esc, focus trap and focus return come from the
 * browser), highlights the size selected in the variant picker, and runs a
 * quick finder for rings (thread cm / inner diameter mm), bracelets (wrist cm)
 * or chains (neck cm). When the result matches a size button on the page,
 * "Select size" picks it in the variant picker.
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class NiyoSizeGuide extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('[data-sg-finder]');
    this.result = this.querySelector('[data-sg-result]');
    this.input = this.form?.querySelector('input[type="number"]');
    this.rows = Array.from(this.querySelectorAll('[data-sg-chart] tbody tr'));
    if (!this.dialog || !this.form || !this.result || !this.input) return;

    this.querySelector('[data-sg-open]')?.addEventListener('click', () => this.open(), { signal });
    this.querySelector('[data-sg-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
    // Click on the backdrop (outside the panel) closes.
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.dialog.close();
      },
      { signal }
    );
    this.form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.find();
      },
      { signal }
    );
    this.form.querySelectorAll('input[type="radio"]').forEach((radio) =>
      radio.addEventListener(
        'change',
        () => {
          this.input.placeholder = radio.dataset.placeholder || '';
          this.input.value = '';
          this.clear();
          this.input.focus();
        },
        { signal }
      )
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** The product section this guide belongs to (scopes the variant picker lookup). */
  get scope() {
    return this.closest('.shopify-section') || document;
  }

  /** Size buttons / options of the page's variant picker, keyed by their value. */
  pickerOptions() {
    const options = new Map();
    this.scope.querySelectorAll('variant-picker input[type="radio"]').forEach((input) => options.set(input.value.trim(), input));
    return options;
  }

  selectedSize() {
    const checked = this.scope.querySelector('variant-picker input[type="radio"]:checked');
    if (checked) return checked.value.trim();
    const select = this.scope.querySelector('variant-picker select');
    return select ? select.value.trim() : null;
  }

  open() {
    const current = this.selectedSize();
    this.rows.forEach((row) => row.classList.toggle('is-current', current !== null && row.dataset.size === current));
    this.dialog.showModal();
    const row = this.rows.find((r) => r.classList.contains('is-current'));
    if (row) this.scrollToRow(row);
  }

  clear() {
    this.result.replaceChildren();
    this.rows.forEach((row) => row.classList.remove('is-match'));
  }

  scrollToRow(row) {
    const wrap = row.closest('.niyo-sg__table-wrap');
    if (!wrap) return;
    const head = wrap.querySelector('thead');
    wrap.scrollTo({ top: row.offsetTop - (head ? head.offsetHeight : 0) - 6, behavior: reduceMotion() ? 'auto' : 'smooth' });
  }

  show(message, row, sizeForPicker) {
    this.clear();
    const text = document.createElement('span');
    text.textContent = message;
    this.result.append(text);
    if (!row) return;
    row.classList.add('is-match');
    this.scrollToRow(row);

    // Offer to select the size when the product sells it.
    const option = sizeForPicker ? this.pickerOptions().get(sizeForPicker) : null;
    if (!option) return;
    if (option.dataset.optionAvailable === 'false' || option.getAttribute('aria-disabled') === 'true') {
      const note = document.createElement('span');
      note.textContent = `Size ${sizeForPicker} is sold out right now.`;
      this.result.append(note);
      return;
    }
    const pick = document.createElement('button');
    pick.type = 'button';
    pick.className = 'niyo-sg__pick';
    pick.textContent = `Select size ${sizeForPicker}`;
    pick.addEventListener('click', () => {
      option.click();
      this.dialog.close();
    });
    this.result.append(pick);
  }

  find() {
    const value = parseFloat(this.input.value);
    if (!(value > 0)) {
      this.clear();
      this.result.textContent = 'Please enter your measurement.';
      return;
    }
    const kind = this.dataset.kind;
    if (kind === 'ring') this.findRing(value);
    else if (kind === 'bracelet') this.findBracelet(value);
    else if (kind === 'chain') this.findChain(value);
  }

  findRing(raw) {
    const method = this.form.querySelector('input[type="radio"]:checked');
    const byDiameter = method?.value === 'diameter';
    const key = byDiameter ? 'diameter' : 'thread';
    // A thread length typed in millimetres (e.g. 54) is read as centimetres.
    const value = !byDiameter && raw > 20 ? raw / 10 : raw;
    // First size at least as big, so the ring isn't tight.
    const row = this.rows.find((r) => parseFloat(r.dataset[key]) >= value - 0.005);
    if (!row) return this.show('That is larger than our chart — please contact us and we will help you find your fit.');
    const first = parseFloat(this.rows[0]?.dataset[key] ?? '0');
    if (row === this.rows[0] && value < first - (byDiameter ? 1 : 0.3)) {
      return this.show(`That is smaller than our chart — please check it is in ${byDiameter ? 'millimetres' : 'centimetres'}.`);
    }
    this.show(`Your ring size is Indian ${row.dataset.size}. Between two sizes? Choose the larger one.`, row, row.dataset.size);
  }

  findBracelet(wrist) {
    const row = this.rows.find((r) => wrist < parseFloat(r.dataset.max) + 0.001);
    if (!row) return this.show('That is larger than our chart — please contact us and we will help you.');
    if (row === this.rows[0] && wrist < parseFloat(row.dataset.min) - 1) return this.show('That is smaller than our chart — please check it is in centimetres.');
    this.show(
      `Size ${row.dataset.size}: a ${row.dataset.length} cm (${row.dataset.inch}″) bracelet. ${(wrist + 1.5).toFixed(1)} cm sits snug, ${(wrist + 2.5).toFixed(1)} cm sits loose.`,
      row,
      null
    );
  }

  findChain(neck) {
    const minimum = neck + 5;
    const row = this.rows.find((r) => parseFloat(r.dataset.length) >= minimum - 0.05);
    if (!row) return this.show('That is longer than our chart — please contact us and we will help you.');
    this.show(`Choose ${row.dataset.size}″ (${row.dataset.length} cm) or longer. ${row.dataset.place}.`, row, null);
  }
}

if (!customElements.get('niyo-size-guide')) customElements.define('niyo-size-guide', NiyoSizeGuide);
